import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { NotificationService, Toast } from '../../core/services/notification.service';

@Component({
  selector: 'app-toast',
  standalone: false,
  template: `
    <div class="toast-stack" aria-live="polite">
      <div
        class="toast"
        *ngFor="let t of toasts"
        [class.toast--success]="t.type === 'success'"
        [class.toast--error]="t.type === 'error'"
        [class.toast--info]="t.type === 'info'"
        [class.toast--warning]="t.type === 'warning'"
      >
        <span class="material-symbols-outlined toast__icon">{{ iconFor(t.type) }}</span>
        <span class="toast__message">{{ t.message }}</span>
        <button class="toast__close" (click)="dismiss(t.id)" aria-label="Cerrar">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>
    </div>
  `,
  styles: [`
    .toast-stack {
      position: fixed;
      bottom: 80px;
      right: 1.5rem;
      z-index: 9999;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      pointer-events: none;
    }

    @media (max-width: 767px) {
      .toast-stack {
        bottom: 80px;
        right: 1rem;
        left: 1rem;
      }
    }

    .toast {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.875rem 1rem;
      border-radius: var(--radius-xl);
      background: var(--surface-container-highest);
      color: var(--on-surface);
      box-shadow: 0 4px 20px rgba(0,0,0,0.18);
      font-size: 0.9rem;
      font-weight: 500;
      max-width: 360px;
      pointer-events: all;
      animation: toast-in 0.25s ease;
      border-left: 4px solid var(--primary);
    }

    @keyframes toast-in {
      from { opacity: 0; transform: translateY(1rem) scale(0.95); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }

    .toast--success { border-left-color: #008c50; }
    .toast--error   { border-left-color: var(--error); }
    .toast--warning { border-left-color: var(--tertiary); }
    .toast--info    { border-left-color: var(--primary); }

    .toast__icon {
      font-size: 1.25rem;
      flex-shrink: 0;
      font-variation-settings: 'FILL' 1;
    }

    .toast--success .toast__icon { color: #008c50; }
    .toast--error   .toast__icon { color: var(--error); }
    .toast--warning .toast__icon { color: var(--tertiary); }
    .toast--info    .toast__icon { color: var(--primary); }

    .toast__message { flex: 1; }

    .toast__close {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--on-surface-variant);
      display: flex;
      align-items: center;
      padding: 0;
      flex-shrink: 0;

      .material-symbols-outlined { font-size: 1rem; }
      &:hover { color: var(--on-surface); }
    }
  `]
})
export class ToastComponent implements OnInit, OnDestroy {
  toasts: Toast[] = [];
  private sub = new Subscription();

  constructor(private notifSvc: NotificationService) {}

  ngOnInit(): void {
    this.sub.add(
      this.notifSvc.toasts$.subscribe(toasts => this.toasts = toasts)
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  iconFor(type: Toast['type']): string {
    const icons: Record<Toast['type'], string> = {
      success: 'check_circle',
      error:   'error',
      warning: 'warning',
      info:    'info'
    };
    return icons[type];
  }

  dismiss(id: string): void {
    this.notifSvc.dismiss(id);
  }
}
